import React, { createContext, useState, useEffect } from 'react';
import shopifyClient from '../utils/ShopifyClient';
import withLoading from '../utils/WithLoading';

const defaultValues = {
  cart: null,
  loading: false,
  didJustAddToCart: false,
  setDidJustAddToCart: () => {},
  addVariantToCart: () => {},
  removeLineItems: () => {},
  updateLineItem: () => {},
  fetchProductById: () => {},
  fetchProductByHandle: () => {},
  createCheckout: () => {},
};

export const StoreContext = createContext(defaultValues);

const isBrowser = typeof window !== `undefined`;
const localStorageKey = `shopify_cart_id`;

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [didJustAddToCart, setDidJustAddToCart] = useState(false);

  const setCartItem = (cart) => {
    if (isBrowser) {
      localStorage.setItem(localStorageKey, cart.id);
    }
    setCart(cart);
  };

  useEffect(() => {
    const initializeCart = async () => {
      await withLoading(
        async () => {
          const existingCartID = isBrowser
            ? localStorage.getItem(localStorageKey)
            : null;

          if (existingCartID && existingCartID !== 'null') {
            try {
              const response = await shopifyClient.request(
                `
                query getCart($cartId: ID!) {
                  cart(id: $cartId) {
                    id
                    lines(first: 100) {
                      edges {
                        node {
                          id
                          quantity
                          merchandise {
                            ... on ProductVariant {
                              id
                              priceV2 {
                                amount
                                currencyCode
                              }
                              product {
                                title
                                handle
                              }
                              weight
                            }
                          }
                        }
                      }
                    }
                  }
                }
              `,
                { cartId: existingCartID }
              );

              if (response.cart) {
                setCartItem(response.cart);
                return;
              }
            } catch (e) {
              localStorage.setItem(localStorageKey, null);
            }
          }

          const response = await shopifyClient.request(`
            mutation createCart {
              cartCreate {
                cart {
                  id
                  lines(first: 100) {
                    edges {
                      node {
                        id
                        quantity
                        merchandise {
                          ... on ProductVariant {
                            id
                            priceV2 {
                              amount
                              currencyCode
                            }
                            product {
                              title
                              handle
                            }
                            weight
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `);
          setCartItem(response.cartCreate.cart);
        },
        () => setLoading(true),
        () => setLoading(false)
      );
    };

    initializeCart();
  }, []);

  const addVariantToCart = async (variantId, quantity) => {
    if (!cart || !cart.id) {
      console.error('Cart not initialized.');
      return;
    }

    const cartId = cart.id;

    await withLoading(
      async () => {
        const response = await shopifyClient.request(
          `
          mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
              cart {
                id
                lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          priceV2 {
                            amount
                            currencyCode
                          }
                          product {
                            title
                            handle
                          }
                          weight
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
          {
            cartId,
            lines: [{ merchandiseId: variantId, quantity }],
          }
        );

        if (response.cartLinesAdd?.cart) {
          setCart(response.cartLinesAdd.cart);
          setDidJustAddToCart(true); // Mark as just added
          setTimeout(() => setDidJustAddToCart(false), 3000); // Reset after 3 seconds
        } else {
          console.error('Failed to add line item:', response);
        }
      },
      () => setLoading(true),
      () => setLoading(false)
    );
  };

  const removeLineItems = async (lineItemId) => {
    if (!cart || !cart.id) {
      console.error('Cart not initialized.');
      return;
    }

    const cartId = cart.id;

    const lineItemIds = Array.isArray(lineItemId) ? lineItemId : [lineItemId];

    await withLoading(
      async () => {
        const response = await shopifyClient.request(
          `
          mutation removeLineItems($cartId: ID!, $lineItemIds: [ID!]!) {
            cartLinesRemove(cartId: $cartId, lineIds: $lineItemIds) {
              cart {
                id
                lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          priceV2 {
                            amount
                            currencyCode
                          }
                          product {
                            title
                            handle
                          }
                          weight
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
          {
            cartId,
            lineItemIds,
          }
        );

        if (response.cartLinesRemove?.cart) {
          setCart(response.cartLinesRemove.cart);
        } else {
          console.error('Failed to remove line items:', response);
        }
      },
      () => setLoading(true),
      () => setLoading(false)
    );
  };

  const updateLineItem = async (lineItemId, quantity) => {
    const cartId = cart.id;

    await withLoading(
      async () => {
        const response = await shopifyClient.request(
          `
          mutation updateLineItem($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                id
                lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          priceV2 {
                            amount
                            currencyCode
                          }
                          product {
                            title
                            handle
                          }
                          weight
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
          {
            cartId,
            lines: [{ id: lineItemId, quantity }],
          }
        );
        setCart(response.cartLinesUpdate.cart);
      },
      () => setLoading(true),
      () => setLoading(false)
    );
  };

  const fetchProductById = async (id) => {
    const query = `
      query getProduct($id: ID!) {
        product(id: $id) {
          id
          title
          variants(first: 99) {
            edges {
              node {
                id
                priceV2 {
                  amount
                  currencyCode
                }
                product {
                  title
                  handle
                }
                weight
                availableForSale
                quantityAvailable
              }
            }
          }
        }
      }
    `;

    const variables = { id };

    try {
      const response = await shopifyClient.request(query, variables);
      return response.product;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  };

  const fetchProductByHandle = async (handle) => {
    const query = `
      query getProductByHandle($handle: String!) {
        productByHandle(handle: $handle) {
          id
          title
          handle
          variants(first: 99) {
            edges {
              node {
                id
                priceV2 {
                  amount
                  currencyCode
                }
                product {
                  title
                  handle
                }
                availableForSale
                quantityAvailable
              }
            }
          }
        }
      }
    `;

    const variables = { handle };

    try {
      const response = await shopifyClient.request(query, variables);
      return response.productByHandle
        ? {
            ...response.productByHandle,
            variants: response.productByHandle.variants.edges.map(
              (edge) => edge.node
            ),
          }
        : null;
    } catch (error) {
      console.error('Error fetching product by handle:', error);
      return null;
    }
  };

  const createCheckout = async () => {
    if (!cart || !cart.id) {
      console.error('Cart not initialized.');
      return null;
    }

    const query = `
      query getCartCheckoutUrl($cartId: ID!) {
        cart(id: $cartId) {
          checkoutUrl
        }
      }
    `;

    try {
      const response = await shopifyClient.request(query, { cartId: cart.id });
      return response.cart?.checkoutUrl || null;
    } catch (error) {
      console.error('Error getting checkout URL:', error);
      return null;
    }
  };

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        cart,
        loading,
        didJustAddToCart,
        addVariantToCart,
        removeLineItems,
        updateLineItem,
        fetchProductById,
        fetchProductByHandle,
        createCheckout,
        setDidJustAddToCart,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export default StoreContext;
